import { useEffect, useRef, useState } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

type Size = Readonly<{
  width: number;
  height: number;
  clientWidth: number;
  clientHeight: number;
}>;

/**
 * Observes element size changes using ResizeObserver.
 * Defers state updates to requestAnimationFrame to avoid ResizeObserver loop warnings.
 *
 * @template T - HTML element type (defaults to HTMLDivElement)
 * @returns Tuple of [size, elementRef] where size is { width, height, clientWidth, clientHeight }
 *
 * @example
 * const [size, ref] = useSizeObserver();
 */
export function useSizeObserver<T extends HTMLElement = HTMLDivElement>() {
  const elementRef = useRef<T>(null);

  const [size, setSize] = useState<Size>({ width: 0, height: 0, clientWidth: 0, clientHeight: 0 });

  useEffect(() => {
    const hasResizeObserver = typeof ResizeObserver !== 'undefined';
    if (!hasResizeObserver) return;

    const element = elementRef.current;
    if (!element) return;

    const observer = createSafeResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;

      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
        clientWidth: entry.target.clientWidth,
        clientHeight: entry.target.clientHeight,
      });
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return [size, elementRef] as const;
}
